// Space-annotation editor (#5, Phase A) — the "draw the space" step.
//
// Optional, BEFORE configuring Smartify products. Two palettes: furniture
// (visualization) and existing electrical points / devices (survey for
// quoting). Tap a palette item to drop a marker, drag to position, tap × to
// remove. A per-room free-text note captures anything icons miss. The existing
// markers + note flow into the lead payload (see CartPage). Shares the room
// photo (IDB `syhouse:room:<divId>`) with RoomEditor2D so it's one canvas.

import { useEffect, useRef, useState, useCallback } from 'react';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import { useWizardStore, type FloorPlacement } from '../store/wizardStore';
import { FURNITURE, EXISTING, paletteIcon, type PaletteItem } from '../config/spacePalette';

const ROOM_PHOTO_KEY = (divId: string) => `syhouse:room:${divId}`;
const MAX_DIM = 1600;

async function compressImage(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const scale = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas unavailable');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.82);
  } finally {
    URL.revokeObjectURL(url);
  }
}

const clamp01 = (v: number) => Math.min(0.97, Math.max(0.03, v));

export function SpaceAnnotationEditor({ divisionId }: { divisionId: string }) {
  const { roomDecor, roomSurveyNote, addDecor, moveDecor, removeDecor, setSurveyNote } =
    useWizardStore();
  const items = roomDecor[divisionId] ?? [];
  const note = roomSurveyNote[divisionId] ?? '';

  const [tab, setTab] = useState<'furniture' | 'existing'>('existing');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const planRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    void idbGet<string>(ROOM_PHOTO_KEY(divisionId)).then((v) => {
      if (!cancelled) setPhotoUrl(v ?? null);
    });
    return () => { cancelled = true; };
  }, [divisionId]);

  async function handleUpload(file: File) {
    const dataUrl = await compressImage(file);
    await idbSet(ROOM_PHOTO_KEY(divisionId), dataUrl);
    setPhotoUrl(dataUrl);
  }
  async function handleRemovePhoto() {
    await idbDel(ROOM_PHOTO_KEY(divisionId));
    setPhotoUrl(null);
  }

  function place(item: PaletteItem) {
    // "Outro" captures a free-text label so the marker is meaningful in the lead.
    let label = item.label;
    let itemNote: string | undefined;
    if (item.type === 'outro') {
      const t = window.prompt('O que existe aqui? (descreve)')?.trim();
      if (!t) return;
      label = t;
      itemNote = t;
    }
    addDecor(divisionId, {
      category: tab,
      type: item.type,
      label,
      x: 0.5,
      y: 0.5,
      ...(itemNote ? { note: itemNote } : {}),
    });
  }

  function posFromPointer(e: { clientX: number; clientY: number }): FloorPlacement | null {
    const rect = planRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01((e.clientY - rect.top) / rect.height),
    };
  }
  function handlePointerMove(e: React.PointerEvent) {
    if (!dragId) return;
    const pos = posFromPointer(e);
    if (pos) moveDecor(divisionId, dragId, pos);
  }
  const stopDrag = useCallback(() => setDragId(null), []);
  useEffect(() => {
    if (!dragId) return;
    window.addEventListener('pointerup', stopDrag);
    return () => window.removeEventListener('pointerup', stopDrag);
  }, [dragId, stopDrag]);

  const palette = tab === 'furniture' ? FURNITURE : EXISTING;

  return (
    <div className="flex h-full min-h-0 flex-col bg-smartify-paper">
      {/* Canvas */}
      <div
        ref={planRef}
        className="relative min-h-0 flex-1 overflow-hidden select-none"
        style={{
          background: photoUrl
            ? `url(${photoUrl}) center/contain no-repeat #f4f4f6`
            : 'repeating-conic-gradient(#e5e7eb 0% 25%, #f4f4f6 0% 50%) 0 0 / 24px 24px',
          touchAction: 'none',
        }}
        onPointerMove={handlePointerMove}
      >
        {items.map((d) => (
          <div
            key={d.id}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              setDragId(d.id);
            }}
            style={{
              position: 'absolute',
              left: `${d.x * 100}%`,
              top: `${d.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              cursor: dragId === d.id ? 'grabbing' : 'grab',
              touchAction: 'none',
            }}
            className="flex flex-col items-center gap-0.5"
          >
            <span
              className={[
                'grid h-10 w-10 place-items-center rounded-full border-2 bg-white text-xl shadow-md',
                d.category === 'existing' ? 'border-smartify-warn' : 'border-white',
              ].join(' ')}
            >
              {paletteIcon(d.type)}
            </span>
            <span className="max-w-[5rem] truncate rounded bg-white/90 px-1 py-px text-[9px] font-medium leading-tight text-smartify-ink shadow">
              {d.label}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeDecor(divisionId, d.id);
              }}
              className="rounded bg-white/80 px-1 py-px text-[9px] leading-tight text-smartify-muted hover:text-red-600"
            >
              ×
            </button>
          </div>
        ))}

        {items.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-6">
            <p className="rounded-smartify bg-white/80 px-4 py-3 text-center text-sm text-smartify-muted">
              Toca num item abaixo para o colocar no espaço. Marca a mobília e <b>o que já existe</b> (tomadas, AC, alarme…).
            </p>
          </div>
        )}

        {/* Photo control */}
        <button
          type="button"
          onClick={() => (photoUrl ? void handleRemovePhoto() : fileRef.current?.click())}
          className="absolute right-2 top-2 rounded-smartify border border-smartify-line bg-white/90 px-2 py-1 text-xs text-smartify-muted hover:border-smartify-brand"
        >
          {photoUrl ? 'Remover foto' : '📷 Foto'}
        </button>
      </div>

      {/* Palette */}
      <div className="shrink-0 border-t border-smartify-line bg-white px-3 py-2">
        <div className="mb-2 flex gap-1.5">
          {([['existing', 'O que já existe'], ['furniture', 'Mobília']] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={[
                'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                tab === key ? 'bg-smartify-brand text-white' : 'bg-smartify-paper text-smartify-muted hover:text-smartify-ink',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {palette.map((p) => (
            <button
              key={p.type}
              type="button"
              onClick={() => place(p)}
              className="flex items-center gap-1 rounded-smartify border border-smartify-line bg-white px-2 py-1 text-xs text-smartify-ink hover:border-smartify-brand"
            >
              <span className="text-base">{p.icon}</span>
              {p.label}
            </button>
          ))}
        </div>

        {/* Per-room free-text note */}
        <textarea
          value={note}
          onChange={(e) => setSurveyNote(divisionId, e.target.value)}
          placeholder="Algo que devamos saber para orçamentar? (ex.: quadro antigo, sem neutro, paredes de betão…)"
          rows={2}
          className="mt-2 w-full resize-none rounded-smartify border border-smartify-line px-2 py-1.5 text-xs focus:border-smartify-brand focus:outline-none"
        />
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleUpload(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
